import {
    PlaneGeometry,
    MeshStandardMaterial,
    Mesh,
    Vector2,
    SRGBColorSpace,
    LinearFilter,
    LinearMipmapLinearFilter,
    RepeatWrapping,
    ShaderChunk,
    Color,
} from 'three'
import Experience from '../Experience.js'

function rand(seed, i) {
    const x = Math.sin(seed + i * 374761393) * 1274126177
    return x - Math.floor(x)
}

export default class Terrain {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.config = this.experience.config
        this.camera = this.experience.camera
        this.debug = this.experience.debug
        this.isMobile = this.experience.isMobile
        this.factor = this.isMobile ? 2 : 1
        this.renderer = this.experience.renderer

        this.debugParams = {
            displacementScale: this.config.terrain.displacementScale,
            emissiveIntensity: this.config.terrain.emissiveIntensity,
            emissiveColor: this.config.terrain.emissiveColor,
            detailRepeat: this.config.terrain.detailRepeat,
            detailIntensity: this.config.terrain.detailIntensity,
            microNormalStrength: this.config.terrain.microNormalStrength,
            reliefStrength: this.config.terrain.reliefStrength ?? 1,
            macroVariation: this.config.terrain.macroVariation ?? 0,
            canopyOcclusion: this.config.terrain.canopyOcclusion ?? 0,
        }

        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder({ title: 'Terrain', expanded: false })
            this.setDebug()
        }

        this.setTextures()
        this.setMaterial()
        this.setGeometry()
        this.setMesh()
        this.prepareHeightData()
        this.prepareTreesDensityData()
    }

    setDebug() {
        this.debugFolder.addBinding(this.debugParams, 'displacementScale', {
            min: 0,
            max: 5,
            step: 0.001
        }).on('change', v => {
            if (this.material) this.material.displacementScale = v.value
        })

        this.debugFolder.addBinding(this.debugParams, 'emissiveIntensity', {
            min: 0,
            max: 5,
            step: 0.001
        }).on('change', v => {
            if (this.material) this.material.emissiveIntensity = v.value
        })

        this.debugFolder.addBinding(this.debugParams, 'emissiveColor', {
            view: 'color'
        }).on('change', v => {
            if (this.material) this.material.emissive.set(v.value)
        })

        this.debugFolder.addBinding(this.debugParams, 'detailRepeat', {
            min: 8,
            max: 60,
            step: 1
        }).on('change', v => {
            if (this.terrainShader?.uniforms?.detailRepeat) {
                this.terrainShader.uniforms.detailRepeat.value = v.value
            }
        })

        this.debugFolder.addBinding(this.debugParams, 'detailIntensity', {
            min: 0,
            max: 0.25,
            step: 0.001
        }).on('change', v => {
            if (this.terrainShader?.uniforms?.detailIntensity) {
                this.terrainShader.uniforms.detailIntensity.value = v.value
            }
        })

        const shaderBinding = (key, options) => {
            this.debugFolder.addBinding(this.debugParams, key, options).on('change', v => {
                const uniform = this.terrainShader?.uniforms?.[key]
                if (uniform) uniform.value = v.value
            })
        }
        shaderBinding('reliefStrength', { min: 0, max: 6, step: 0.01 })
        shaderBinding('macroVariation', { min: 0, max: 0.3, step: 0.001 })
        shaderBinding('canopyOcclusion', { min: 0, max: 1, step: 0.01 })

        this.debugFolder.addBinding(this.debugParams, 'microNormalStrength', {
            min: 0,
            max: 1,
            step: 0.01
        }).on('change', v => {
            if (this.material) {
                this.material.normalScale.set(v.value, v.value)
            }
        })
    }

    setTextures() {
        const maxAnisotropy =
            this.renderer.instance.capabilities.getMaxAnisotropy()

        this.textures = {}

        // =====================================================
        // BASE COLOR
        // =====================================================

        this.textures.colorMap = this.resources.items.colorMap
        this.textures.colorMap.colorSpace = SRGBColorSpace
        this.textures.colorMap.minFilter = LinearMipmapLinearFilter
        this.textures.colorMap.magFilter = LinearFilter
        this.textures.colorMap.generateMipmaps = true
        this.textures.colorMap.anisotropy = maxAnisotropy

        // =====================================================
        // HEIGHT
        // =====================================================

        this.textures.heightMap = this.resources.items.heightMap
        this.textures.heightMap.minFilter = LinearMipmapLinearFilter
        this.textures.heightMap.magFilter = LinearFilter
        this.textures.heightMap.generateMipmaps = true
        this.textures.heightMap.anisotropy = maxAnisotropy

        // =====================================================
        // ALPHA
        // =====================================================

        this.textures.alphaMap = this.resources.items.alphaMap
        this.textures.alphaMap.minFilter = LinearMipmapLinearFilter
        this.textures.alphaMap.magFilter = LinearFilter
        this.textures.alphaMap.generateMipmaps = true
        this.textures.alphaMap.anisotropy = maxAnisotropy

        // =====================================================
        // ROUGHNESS
        // =====================================================

        this.textures.roughnessMap = this.resources.items.roughnessMap
        this.textures.roughnessMap.minFilter = LinearMipmapLinearFilter
        this.textures.roughnessMap.magFilter = LinearFilter
        this.textures.roughnessMap.generateMipmaps = true
        this.textures.roughnessMap.anisotropy = maxAnisotropy

        // =====================================================
        // TREES DENSITY
        // =====================================================

        this.textures.treesDensityMap = this.resources.items.vegetation
        this.textures.treesDensityMap.minFilter = LinearMipmapLinearFilter
        this.textures.treesDensityMap.magFilter = LinearFilter
        this.textures.treesDensityMap.generateMipmaps = true
        this.textures.treesDensityMap.anisotropy = maxAnisotropy

        // =====================================================
        // MASK MAP
        // IMPORTANT:
        // - mipmaps ON
        // - anisotropy ON
        // -> évite bords pixelisés / scintillement
        // =====================================================

        this.textures.maskMap = this.resources.items.maskMap
        this.textures.maskMap.minFilter = LinearMipmapLinearFilter
        this.textures.maskMap.magFilter = LinearFilter
        this.textures.maskMap.generateMipmaps = true
        this.textures.maskMap.anisotropy = maxAnisotropy

        // =====================================================
        // DETAIL MAPS
        // =====================================================

        const detailMaps = [
            'greenDetailMap',
            'fairwayDetailMap',
            'bunkerDetailMap'
        ]

        detailMaps.forEach((key) => {
            const tex = this.resources.items[key]

            tex.wrapS = RepeatWrapping
            tex.wrapT = RepeatWrapping

            tex.minFilter = LinearMipmapLinearFilter
            tex.magFilter = LinearFilter

            tex.generateMipmaps = true
            tex.anisotropy = maxAnisotropy

            this.textures[key] = tex
        })

        this.textures.microNormalMap = this.resources.items.waterNormalMap
        this.textures.microNormalMap.wrapS = RepeatWrapping
        this.textures.microNormalMap.wrapT = RepeatWrapping
        this.textures.microNormalMap.repeat.set(18, 18)
        this.textures.microNormalMap.minFilter = LinearMipmapLinearFilter
        this.textures.microNormalMap.magFilter = LinearFilter
        this.textures.microNormalMap.generateMipmaps = true
        this.textures.microNormalMap.anisotropy = maxAnisotropy
    }

    setMaterial() {

        this.material = new MeshStandardMaterial({
            map: this.textures.colorMap,

            displacementMap: this.textures.heightMap,
            displacementScale: this.debugParams.displacementScale,
            displacementBias: 0,

            roughnessMap: this.textures.roughnessMap,
            normalMap: this.textures.microNormalMap,
            normalScale: new Vector2(this.debugParams.microNormalStrength, this.debugParams.microNormalStrength),
            alphaMap: this.textures.alphaMap,

            transparent: true,
            fog: true,

            dithering: true
        })

        this.material.onBeforeCompile = (shader) => {

            // =====================================================
            // UNIFORMS
            // =====================================================

            shader.uniforms.maskMap = {
                value: this.textures.maskMap
            }

            shader.uniforms.greenDetailMap = {
                value: this.textures.greenDetailMap
            }

            shader.uniforms.fairwayDetailMap = {
                value: this.textures.fairwayDetailMap
            }

            shader.uniforms.bunkerDetailMap = {
                value: this.textures.bunkerDetailMap
            }

            shader.uniforms.detailRepeat = {
                value: this.debugParams.detailRepeat
            }

            shader.uniforms.detailIntensity = {
                value: this.debugParams.detailIntensity
            }

            // Relief : la heightmap déplace les sommets mais la normale
            // restait celle d'un plan plat, les collines ne prenaient donc
            // pas la lumière. On la reconstruit par pixel depuis la heightmap.
            const heightImage = this.textures.heightMap.image
            shader.uniforms.reliefMap = { value: this.textures.heightMap }
            shader.uniforms.reliefTexel = {
                value: new Vector2(1 / (heightImage?.width || 1024), 1 / (heightImage?.height || 1024))
            }
            shader.uniforms.reliefScale = {
                value: this.debugParams.displacementScale / this.geometry.parameters.width
            }
            shader.uniforms.reliefStrength = { value: this.debugParams.reliefStrength }

            shader.uniforms.canopyMap = { value: this.textures.treesDensityMap }
            shader.uniforms.canopyOcclusion = { value: this.debugParams.canopyOcclusion }
            shader.uniforms.macroVariation = { value: this.debugParams.macroVariation }
            shader.uniforms.stripeSoftening = { value: this.config.terrain.stripeSoftening ?? 0 }
            shader.uniforms.greenToning = { value: this.config.terrain.greenToning ?? 0 }
            shader.uniforms.uTime = { value: 0 }
            shader.uniforms.waterMap = { value: this.textures.roughnessMap }
            shader.uniforms.waterColor = { value: new Color(this.config.terrain.waterColor ?? '#0b1612') }

            // =====================================================
            // COMMON
            // =====================================================

            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <common>',
                `
                #include <common>
    
                uniform sampler2D maskMap;
    
                uniform sampler2D greenDetailMap;
                uniform sampler2D fairwayDetailMap;
                uniform sampler2D bunkerDetailMap;
    
                uniform float detailRepeat;
                uniform float detailIntensity;

                uniform sampler2D reliefMap;
                uniform vec2 reliefTexel;
                uniform float reliefScale;
                uniform float reliefStrength;

                uniform sampler2D canopyMap;
                uniform float canopyOcclusion;
                uniform float macroVariation;
                uniform float stripeSoftening;
                uniform float greenToning;
                uniform float uTime;
                uniform vec3 waterColor;
                uniform sampler2D waterMap;

                // L'eau n'existe que dans la roughnessMap (rugosité ~0) :
                // elle sert de masque pour un traitement dédié.
                float waterMaskAt(vec2 uv) {
                    return 1.0 - smoothstep(0.06, 0.38, texture2D(waterMap, uv).g);
                }

                float terrainHash(vec2 p) {
                    p = fract(p * vec2(123.34, 456.21));
                    p += dot(p, p + 45.32);
                    return fract(p.x * p.y);
                }

                float terrainNoise(vec2 p) {
                    vec2 i = floor(p);
                    vec2 f = fract(p);
                    vec2 u = f * f * (3.0 - 2.0 * f);
                    return mix(
                        mix(terrainHash(i), terrainHash(i + vec2(1.0, 0.0)), u.x),
                        mix(terrainHash(i + vec2(0.0, 1.0)), terrainHash(i + vec2(1.0, 1.0)), u.x),
                        u.y
                    );
                }

                // Gradient de la heightmap sur deux rayons : le large lisse
                // les marches de quantification 8 bits, le fin garde le modelé.
                vec2 reliefGradient(vec2 uv, float radius) {
                    vec2 o = reliefTexel * radius;
                    float hl = texture2D(reliefMap, uv - vec2(o.x, 0.0)).r;
                    float hr = texture2D(reliefMap, uv + vec2(o.x, 0.0)).r;
                    float hd = texture2D(reliefMap, uv - vec2(0.0, o.y)).r;
                    float hu = texture2D(reliefMap, uv + vec2(0.0, o.y)).r;
                    return vec2(hr - hl, hu - hd) / (2.0 * o);
                }
                `
            )

            // Normale de relief (espace objet du plan -> monde -> vue).
            // Le plan est tourné de -90° sur X : (x, y, z) -> (x, z, -y).
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <normal_fragment_begin>',
                ShaderChunk.normal_fragment_begin.replace(
                    'vec3 normal = normalize( vNormal );',
                    `
                    vec2 reliefGrad = mix(
                        reliefGradient(vMapUv, 2.5),
                        reliefGradient(vMapUv, 7.0),
                        0.55
                    ) * reliefScale * reliefStrength;
                    vec3 reliefObject = normalize(vec3(-reliefGrad.x, -reliefGrad.y, 1.0));
                    vec3 reliefWorld = vec3(reliefObject.x, reliefObject.z, -reliefObject.y);
                    vec3 normal = normalize((viewMatrix * vec4(reliefWorld, 0.0)).xyz);
                    `
                )
            )

            // =====================================================
            // MAP FRAGMENT
            // =====================================================

            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <map_fragment>',
                `
                #include <map_fragment>
    
                vec2 uv = vMapUv;
    
                // =================================================
                // MASK SAMPLING
                // =================================================
    
                vec3 mask = texture2D(maskMap, uv).rgb;
    
                // Anti aliasing basé dérivées écran
                float aaR = fwidth(mask.r);
                float aaG = fwidth(mask.g);
                float aaB = fwidth(mask.b);
    
                // Transitions propres
                float fairwayMask = smoothstep(
                    0.5 - aaR,
                    0.5 + aaR,
                    mask.r
                );
    
                float greenMask = smoothstep(
                    0.5 - aaG,
                    0.5 + aaG,
                    mask.g
                );
    
                float bunkerMask = smoothstep(
                    0.5 - aaB,
                    0.5 + aaB,
                    mask.b
                );
    
                // =================================================
                // NORMALIZATION
                // =================================================
    
                float total =
                    fairwayMask +
                    greenMask +
                    bunkerMask;
    
                total = max(total, 0.0001);
    
                fairwayMask /= total;
                greenMask /= total;
                bunkerMask /= total;
    
                // =================================================
                // DETAIL UV
                // =================================================
    
                vec2 detailUv = uv * detailRepeat;
    
                // =================================================
                // DETAIL TEXTURES
                // =================================================
    
                vec3 fairwayDetail =
                    texture2D(fairwayDetailMap, detailUv).rgb;
    
                vec3 greenDetail =
                    texture2D(greenDetailMap, detailUv).rgb;
    
                vec3 bunkerDetail =
                    texture2D(bunkerDetailMap, detailUv).rgb;
    
                // =================================================
                // DETAIL CONTRAST
                // =================================================
    
                fairwayDetail =
                    (fairwayDetail - 0.5) * 2.0;
    
                greenDetail =
                    (greenDetail - 0.5) * 5.0;
    
                bunkerDetail =
                    (bunkerDetail - 0.5) * 5.0;
    
                // =================================================
                // DOMINANT LAYER
                // -> évite halos / flou
                // =================================================
    
                vec3 detail = vec3(0.0);
    
                if (
                    bunkerMask > greenMask &&
                    bunkerMask > fairwayMask
                ) {
                    detail = bunkerDetail;
                }
                else if (
                    greenMask > fairwayMask
                ) {
                    detail = greenDetail;
                }
                else {
                    detail = fairwayDetail;
                }
    
                // =================================================
                // APPLY
                // =================================================
    
                // =================================================
                // MOWING & GREENS
                // -> bandes de tonte adoucies (moyenne locale via un mip
                //    plus flou), greens ramenés d'un vert néon à un vert
                //    profond et dense
                // =================================================

                vec3 localMean = texture2D(map, uv, 4.0).rgb;
                diffuseColor.rgb = mix(diffuseColor.rgb, localMean, fairwayMask * stripeSoftening);

                float greenLuma = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
                vec3 greenTone = mix(vec3(greenLuma), diffuseColor.rgb, 0.62) * vec3(0.8, 0.87, 0.76);
                diffuseColor.rgb = mix(diffuseColor.rgb, greenTone, greenMask * greenToning);

                diffuseColor.rgb += detail * detailIntensity;

                // =================================================
                // MACRO VARIATION
                // -> casse l'uniformité des grandes surfaces d'herbe
                //    (zones plus sèches, tontes irrégulières)
                // =================================================

                float macroA = terrainNoise(uv * 38.0);
                float macroB = terrainNoise(uv * 140.0 + 7.3);
                float macro = macroA * 0.65 + macroB * 0.35;
                float macroWeight = 1.0 - greenMask * 0.75 - bunkerMask * 0.6;
                diffuseColor.rgb *= 1.0 + (macro - 0.5) * 2.0 * macroVariation * macroWeight;

                float dryPatch = smoothstep(0.58, 0.9, terrainNoise(uv * 16.0 + 3.1));
                diffuseColor.rgb = mix(
                    diffuseColor.rgb,
                    diffuseColor.rgb * vec3(1.07, 1.02, 0.84),
                    dryPatch * macroWeight * (1.0 - fairwayMask * 0.5) * 0.55
                );

                // =================================================
                // CANOPY OCCLUSION
                // -> sol plus sombre et plus froid sous les bois
                // =================================================

                float canopy = smoothstep(0.04, 0.55, texture2D(canopyMap, uv).r);
                diffuseColor.rgb *= mix(vec3(1.0), vec3(0.62, 0.66, 0.6), canopy * canopyOcclusion);

                // =================================================
                // WATER
                // -> eau profonde : absorbe la lumière, le ciel ne s'y
                //    lit que par reflet (Fresnel), plus de turquoise plat
                // =================================================

                float waterMask = waterMaskAt(vRoughnessMapUv);
                diffuseColor.rgb = mix(diffuseColor.rgb, waterColor, waterMask * 0.9);

                `
            )

            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <roughnessmap_fragment>',
                `
                #include <roughnessmap_fragment>
                roughnessFactor = mix(roughnessFactor, 0.07, waterMask);
                `
            )

            // Rides lentes sur l'eau : deux couches de la normal map qui
            // dérivent en sens opposés.
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <normal_fragment_maps>',
                `
                #include <normal_fragment_maps>
                if (waterMask > 0.001) {
                    vec2 rippleUv = vNormalMapUv * 2.2;
                    vec3 rippleA = texture2D(normalMap, rippleUv + vec2(uTime * 0.011, uTime * 0.007)).xyz * 2.0 - 1.0;
                    vec3 rippleB = texture2D(normalMap, rippleUv * 1.63 - vec2(uTime * 0.008, -uTime * 0.012)).xyz * 2.0 - 1.0;
                    vec3 ripple = normalize(vec3((rippleA.xy + rippleB.xy) * 0.16, 1.0));
                    normal = normalize(mix(normal, normalize(tbn * ripple), waterMask));
                }
                `
            )

            this.terrainShader = shader
        }

        this.material.needsUpdate = true
    }

    prepareHeightData() {
        const img = this.textures.heightMap.image
        if (!img) return

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { willReadFrequently: true })

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        this.heightPixels = ctx.getImageData(0, 0, img.width, img.height).data
        this.heightWidth = img.width
        this.heightHeight = img.height
    }

    prepareTreesDensityData() {
        const img = this.textures.treesDensityMap.image
        if (!img) return

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { willReadFrequently: true })

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        this.treesPixels = ctx.getImageData(0, 0, img.width, img.height).data
        this.treesWidth = img.width
        this.treesHeight = img.height
    }

    getTreeDensityAt(x, z) {
        if (!this.treesPixels) return 0

        const u = (x / this.geometry.parameters.width) + 0.5
        const v = (z / this.geometry.parameters.height) + 0.5

        const uu = Math.min(Math.max(u, 0), 1)
        const vv = Math.min(Math.max(v, 0), 1)

        const px = Math.floor(uu * (this.treesWidth - 1))
        const py = Math.floor(vv * (this.treesHeight - 1))

        const index = (py * this.treesWidth + px) * 4

        return this.treesPixels[index] / 255
    }

    setGeometry() {
        this.geometry = new PlaneGeometry(
            10,
            10,
            128 / this.factor,
            128 / this.factor
        )
    }

    setMesh() {
        this.mesh = new Mesh(this.geometry, this.material)
        this.mesh.rotation.x = -Math.PI / 2
        this.mesh.renderOrder = 0

        this.scene.add(this.mesh)
    }

    getHeightAt(x, z) {
        if (!this.heightPixels) return 0

        const u = (x / this.geometry.parameters.width) + 0.5
        const v = (z / this.geometry.parameters.height) + 0.5

        const uu = Math.min(Math.max(u, 0), 1)
        const vv = Math.min(Math.max(v, 0), 1)

        const px = Math.floor(uu * (this.heightWidth - 1))
        const py = Math.floor(vv * (this.heightHeight - 1))

        const index = (py * this.heightWidth + px) * 4
        const h = this.heightPixels[index] / 255

        return h * this.material.displacementScale
    }

    // Hauteur interpolée (bilinéaire) : continue d'un pixel à l'autre,
    // pour suivre le relief en mouvement sans marches d'escalier.
    getSmoothHeightAt(x, z) {
        if (!this.heightPixels) return 0

        const u = Math.min(Math.max((x / this.geometry.parameters.width) + 0.5, 0), 1)
        const v = Math.min(Math.max((z / this.geometry.parameters.height) + 0.5, 0), 1)

        const fx = u * (this.heightWidth - 1)
        const fy = v * (this.heightHeight - 1)
        const x0 = Math.floor(fx)
        const y0 = Math.floor(fy)
        const x1 = Math.min(x0 + 1, this.heightWidth - 1)
        const y1 = Math.min(y0 + 1, this.heightHeight - 1)
        const tx = fx - x0
        const ty = fy - y0

        const at = (px, py) => this.heightPixels[(py * this.heightWidth + px) * 4] / 255
        const top = at(x0, y0) + (at(x1, y0) - at(x0, y0)) * tx
        const bottom = at(x0, y1) + (at(x1, y1) - at(x0, y1)) * tx

        return (top + (bottom - top) * ty) * this.material.displacementScale
    }

    update() {
        if (this.terrainShader) {
            this.terrainShader.uniforms.uTime.value = this.experience.time.elapsed * 0.001
        }
    }

    destroy() {
        if (this.mesh) {
            this.scene.remove(this.mesh)
            this.mesh.geometry?.dispose()
            this.mesh.material?.dispose()
            this.mesh = null
        }

        this.heightPixels = null
        this.treesPixels = null
        this.textures = null
    }
}